// Vue core libraries - Pinia, VueUse & Vue
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";
import { computed, ComputedRef, ref } from "vue";

// TMI - content type and query definitions
import { Product } from "@tmi/types/entities";

// Authentication and Authorization services & mapping for adjusting the "slug" for the rebranded programs
import { useAuthStore } from "@/stores/Auth";
import { usePermissionsStore } from "@/stores/Permissions";

import LocalForagePlugin, { products } from "@/plugins/LocalForagePlugin";
import { productSeedData } from "@/services/seed-service";
import { refreshProductsFromServer } from "@/services/data/sync/product-sync-coordinator";

// Non-reactive variables
import { createDefaultLogger } from "@/types/console-logger";
const logger = createDefaultLogger();

const defaultProducts = [] as unknown as Product[];
const allProducts = ref< Product[] >( defaultProducts );

// cache a separate list of the products that are marked as "display-able"
const displayableProducts = computed< Product[] >( () => allProducts.value.filter( ( product: Product ) => product.isDisplay ) );

const initialized = computed< boolean >( () => allProducts.value.length > 0 );
const isLoading = computed( () => loading.value );
const loading = ref< boolean >( false );

export const useProductsStore = defineStore( 'products', () => {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const permissionsStore = usePermissionsStore();

    function reset(): void {
        allProducts.value.splice( 0 );
        loading.value = false;
    }

    async function initializeAsync(): Promise< void > {
        if ( initialized.value || loading.value ) {
            logger.debug( '[ Products ].initialize() exiting - already processing...' );
            return;
        }

        loading.value = true;

        try {
            const t0: number = performance.now();

            // Wait for the driver to settle — unconditionally. The bindings are assigned synchronously,
            // but an instance's own ready() resolves before sibling stores finish upgrading the shared
            // database on a fresh origin; reading in that window hangs localforage (fresh-origin boot race).
            await LocalForagePlugin.initialize();

            await products.ready();
            const numberProducts = await products.length();

            const uniqueProducts = new Map< string, Product >();
            if ( numberProducts > 0 ) {
                // Clear the contents of the collections
                allProducts.value.splice( 0 );

                // Iterate over all elements within IDB
                await products.iterate( ( product: Product ) => {
                    uniqueProducts.set( product.id, product );
                    return;
                } );

                allProducts.value = [ ...( Array.from( uniqueProducts.values() ) ) ]
                    .sort( ( prod1: Product, prod2: Product ): number =>
                        ( prod1.name.toLowerCase() > prod2.name.toLowerCase() ) ? 1 :
                            ( ( prod1.name.toLowerCase() < prod2.name.toLowerCase() ) ? -1 : 0 ) );

                const t1: number = performance.now();
                logger.info( `(local) Loaded ${ allProducts.value.length } products in ${ t1 - t0 } ms` );
            } else {
                allProducts.value = productSeedData();

                const t1: number = performance.now();
                logger.info( `Fetching & mapping ${ allProducts.value.length } product items took ${ t1 - t0 } ms` );
            }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
            reset();
        } finally {
            // Clear the "loading" flag
            loading.value = false;
        }
    }

    const productCatalog = computed< Product[] >(() => displayableProducts.value );

    function productById( productSysId: string ): Product | undefined {
        return allProducts.value.find( ( product: Product ): boolean => product.id === productSysId );
    }

    function productBySlug( slug: string ): Product | undefined {
        return allProducts.value.find( ( product: Product ): boolean => product.slug === slug );
    }

    function productByProductCode ( code: string ): Product | undefined {
        return allProducts.value.find( ( product: Product ): boolean => {
            const productSlugs = product.codes ? product.codes.split( ',' ) : [];  // Convert codes's into an array
            return productSlugs.includes( code ); // Check if the single slug exists in the product's slug list
        } );
    }

    /**
     * Returns the collection of all products for which the user has an active subscription.
     *
     * @returns {ComputedRef< Product[] >} - all products with an active subscription
     */
    const subscribedProducts: ComputedRef< Product[] > = computed( () => filterProductsBySubscription( true ) );

    /**
     * Returns the collection of all products that remain after filtering out any products for which
     * the user has an active subscription.
     *
     * @returns {ComputedRef< Product[] >} - the collection of products that are available for the user to purchase
     */
    const availableProducts: ComputedRef< Product[] > = computed( () => filterProductsBySubscription( false ) );

    /**
     * Filters out non-displayable products
     *
     * @param subscribedFlag - a flag to switch between whether the collection is to contain products for which the
     * user has an active subscription
     * @returns {Product[]} - the collection of products that are either currently owned by the or the inverse of this.
     */
    function filterProductsBySubscription( subscribedFlag: boolean ): Product[] {
        let filteredProducts: Product[] = [];
        // If the product list for the non-subscribed offerings is not yet initialized & the user is logged in...
        if ( isAuthenticated.value ) {
            const products: Product[] = displayableProducts.value.filter( ( item: Product ) => {
                const itemCodes: string = item?.codes ?? '';

                let include: boolean = false;
                if ( itemCodes ) {
                    // Filter out anything that is not defined as "display-able" as well as if the user doesn't have access
                    const hasAccess: boolean = permissionsStore.hasAccessBasedOnShortCode( itemCodes );
                    include = subscribedFlag ? hasAccess : !hasAccess;
                }

                return include;
            } );

            filteredProducts = [ ...products ];
        } else if ( !subscribedFlag ) {
            // If the user is not logged in and the filter is looking for all non-subscribed products - assign all products that are display-able
            filteredProducts = displayableProducts.value;
        }

        return filteredProducts;
    }

    async function refreshAsync(): Promise< void > {
        allProducts.value = await refreshProductsFromServer();
    }

    return {
        // ACTIONS
        availableProducts,
        initializeAsync,
        productById,
        productBySlug,
        productByProductCode,
        subscribedProducts,
        reset,
        refreshAsync,

        // GETTERS
        allProducts,
        displayableProducts,
        isLoading,
        productCatalog
    }
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useProductsStore, import.meta.hot ) );
}
